import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QrCode, ScanLine, Sparkles } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import QRScanner from "@/components/QRScanner";
import QRGenerator from "@/components/QRGenerator";
import HistoryPanel from "@/components/HistoryPanel";
import { readHistory } from "@/lib/qrHistory";

const TABS = [
  { id: "scanner", label: "Tarayıcı", icon: ScanLine },
  { id: "generator", label: "Oluşturucu", icon: QrCode },
];

function Home() {
  const [tab, setTab] = useState("scanner");
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setHistory(readHistory());
  }, []);

  const refreshHistory = () => setHistory(readHistory());
  const handleHistoryChange = (maybeList) => {
    if (Array.isArray(maybeList)) setHistory(maybeList);
    else refreshHistory();
  };

  return (
    <div className="qr-app-bg">
      {/* Sticky glass header */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm shadow-blue-600/30">
              <QrCode className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="font-display font-bold text-slate-900 text-lg leading-none">
                QRexpress
              </h1>
              <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                Tek tıkla oku · Tek tıkla oluştur
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-full px-3 py-1.5">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
            <span className="font-semibold text-blue-700">Ücretsiz</span>
            <span>· Sınırsız kullanım</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Hero */}
        <section className="mb-8 lg:mb-10">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 max-w-3xl">
            QR kodları{" "}
            <span className="text-blue-600">saniyeler içinde</span> okuyun ve oluşturun.
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl text-base">
            Kayıt gerekmez, ücretsizdir. Kameranızı açın veya metninizi yazın;
            gerisini biz hallederiz.
          </p>
        </section>

        {/* Tabs pill */}
        <div
          data-testid="tabs-root"
          className="inline-flex items-center p-1 bg-white border border-slate-200 rounded-full shadow-sm mb-6"
        >
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                data-testid={`tab-${id}-trigger`}
                onClick={() => setTab(id)}
                className="relative px-5 sm:px-6 h-11 rounded-full text-sm font-semibold transition-colors active:scale-95 inline-flex items-center gap-2"
              >
                {active && (
                  <motion.span
                    layoutId="tab-pill"
                    className="absolute inset-0 bg-blue-600 rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <Icon
                  className={`w-4 h-4 relative z-10 ${
                    active ? "text-white" : "text-slate-600"
                  }`}
                  strokeWidth={2.2}
                />
                <span
                  className={`relative z-10 ${
                    active ? "text-white" : "text-slate-700"
                  }`}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div
              data-testid="tool-surface"
              className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 lg:p-8 shadow-sm"
            >
              <AnimatePresence mode="wait">
                {tab === "scanner" ? (
                  <motion.div
                    key="scanner"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SectionHeading
                      eyebrow="Canlı Tarama"
                      title="Kameranızla QR kod okuyun"
                      description="Arka kamera otomatik olarak seçilir. QR kodu çerçevenin içine hizalayın."
                    />
                    <QRScanner onHistoryChange={handleHistoryChange} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="generator"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SectionHeading
                      eyebrow="QR Oluşturucu"
                      title="İhtiyacınıza uygun QR kodu üretin"
                      description="URL, metin, Wi-Fi, e-posta, telefon veya SMS — yazdıkça anlık güncellenir."
                    />
                    <QRGenerator onHistoryChange={handleHistoryChange} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <HistoryPanel items={history} onChange={handleHistoryChange} />
          </aside>
        </div>

        <footer className="mt-12 text-center text-xs text-slate-400 space-y-1">
          <p>
            QRexpress — tüm işlemler cihazınızda gerçekleşir. Veriler sunucuya
            gönderilmez.
          </p>
          <p className="text-slate-500 font-semibold tracking-wide">
            © {new Date().getFullYear()} TELL CELL · Tüm hakları saklıdır.
          </p>
        </footer>
      </main>

      <Toaster position="top-center" richColors />
    </div>
  );
}

function SectionHeading({ eyebrow, title, description }) {
  return (
    <div className="mb-6">
      <span className="inline-block text-[11px] tracking-[0.12em] uppercase font-bold text-blue-600">
        {eyebrow}
      </span>
      <h3 className="font-display font-bold text-xl sm:text-2xl text-slate-900 mt-1">
        {title}
      </h3>
      <p className="text-sm text-slate-600 mt-1 max-w-xl">{description}</p>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
